import { type Qualification } from "@clipboard-health/contract-worker-app-bff";
import { Box, DialogContent, Stack } from "@mui/material";
import { useDebounce } from "@src/appV2/lib/utils/useDebounce";
import { useAutoApprovedQualifications } from "@src/appV2/Licenses/useAutoApprovedQualifications";
import { useWorkerVisibleQualifications } from "@src/appV2/Qualifications/api/useWorkerVisibleQualifications";
import {
  BottomSheet,
  Input,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Text,
  type UseModalState,
} from "@src/appV2/redesign/system/ui-components";
import { useCallback, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { generatePath, useHistory } from "react-router-dom";

import { BottomSheetHeader } from "../../components/BottomSheetHeader";
import { DialogFooter } from "../../components/DialogFooter";
import { type AddLicenseFormData } from "./FormProvider";

interface AddLicenseAddQualificationBottomSheetProps {
  modalState: UseModalState;
  addLicensePath: string;
}

export function AddLicenseAddQualificationBottomSheet(
  props: AddLicenseAddQualificationBottomSheetProps
) {
  const { modalState, addLicensePath } = props;

  const [searchTerm, setSearchTerm] = useState("");
  const history = useHistory();

  const { setValue } = useFormContext<AddLicenseFormData>();

  const debouncedSearchQuery = useDebounce({ value: searchTerm, debounceTimeInMs: 300 });

  const { data: qualifications } = useWorkerVisibleQualifications();

  const autoApprovedQualification = useAutoApprovedQualifications();

  // todo: we need to filter only by qualifications that we allow adding a license
  const filteredQualifications = useMemo(() => {
    const qualificationsData = (qualifications?.data ?? []).filter(
      (qualification) => !autoApprovedQualification.has(qualification.attributes.name)
    );

    if (!debouncedSearchQuery.trim()) {
      return qualificationsData.sort((a, b) =>
        a.attributes.displayLabel.localeCompare(b.attributes.displayLabel)
      );
    }

    return qualificationsData.filter((qualification) => {
      const qualificationSearchString = `${qualification.attributes.name.toLowerCase()} ${qualification.attributes.displayLabel.toLowerCase()}`;
      return qualificationSearchString.includes(debouncedSearchQuery.toLowerCase());
    });
  }, [debouncedSearchQuery, qualifications, autoApprovedQualification]);

  const handleQualificationSelect = (qualification: Qualification) => {
    setValue("qualificationName", qualification.attributes.displayLabel);
    history.replace(
      generatePath(addLicensePath, {
        qualificationId: qualification.id,
      })
    );
  };

  const handleClose = useCallback(() => {
    modalState.closeModal();
  }, [modalState]);

  return (
    <BottomSheet
      footer={<DialogFooter orientation="vertical" />}
      header={<BottomSheetHeader onClose={handleClose} />}
      modalState={modalState}
    >
      <DialogContent sx={{ padding: 5, paddingTop: 8, height: "90vh" }}>
        <Stack spacing={3}>
          <Stack spacing={7}>
            <Text semibold variant="h4">
              Enter qualification
            </Text>
            <Input
              size="medium"
              placeholder="Qualification"
              value={searchTerm}
              onChange={(event) => {
                setSearchTerm(event.target.value);
              }}
            />
          </Stack>
          <Box sx={{ paddingX: 3 }}>
            <List dense>
              {filteredQualifications.map((qualification) => (
                <ListItem key={qualification.id}>
                  <ListItemButton
                    onClick={() => {
                      handleQualificationSelect(qualification);
                    }}
                  >
                    <ListItemText>{qualification.attributes.displayLabel}</ListItemText>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
